import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';

export default function RentalsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    moveInDate: '',
    budget: '',
    bedrooms: '',
    pets: '',
    employmentStatus: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [heroLoaded, setHeroLoaded] = useState(false);
  const parallaxRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (parallaxRef.current) {
        const offset = window.scrollY * 0.5;
        parallaxRef.current.style.transform = `translateY(${offset}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // SEO: Set document title and meta tags
  useEffect(() => {
    document.title = 'Miami Rental Properties | Available Apartments & Homes | Miami Comfort Group';
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Browse pre-screened rental properties in Miami-Dade. Licensed agent, MLS verified, bilingual EN/ES. Apply online today.');

    // Update keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'Miami rental properties, apartments for rent Miami, MLS verified rentals, Miami-Dade rentals, bilingual real estate agent');

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/rentals`);

    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Open Graph / Facebook
    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${siteUrl}/rentals` },
      { property: 'og:title', content: 'Miami Rental Properties | Available Apartments & Homes' },
      { property: 'og:description', content: 'Browse pre-screened rental properties in Miami-Dade. Licensed agent, MLS verified, bilingual EN/ES. Apply online today.' },
      { property: 'og:image', content: 'https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/142ce1355e7fe0cb96ea69f1f0b28b9d.jpeg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:locale', content: 'en_US' }
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Card
    const twitterTags = [
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: `${siteUrl}/rentals` },
      { property: 'twitter:title', content: 'Miami Rental Properties | Available Apartments & Homes' },
      { property: 'twitter:description', content: 'Browse pre-screened rental properties in Miami-Dade. Licensed agent, MLS verified, bilingual EN/ES. Apply online today.' },
      { property: 'twitter:image', content: 'https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/142ce1355e7fe0cb96ea69f1f0b28b9d.jpeg' }
    ];

    twitterTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Add JSON-LD schema
    const rentalsPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Miami Rental Properties",
      "description": "Browse pre-screened rental properties in Miami-Dade. Licensed agent, MLS verified, bilingual EN/ES.",
      "url": `${siteUrl}/rentals`,
      "mainEntity": {
        "@type": "RealEstateAgent",
        "name": "Miami Comfort Group",
        "description": "Browse pre-screened rental properties in Miami-Dade. Licensed agent, MLS verified, bilingual EN/ES. Apply online today."
      }
    };

    let schemaScript = document.querySelector('script[data-schema="rentals"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'rentals');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(rentalsPageSchema);

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Miami Comfort Group';
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const formBody = new URLSearchParams();
    formBody.append('fullName', formData.fullName);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('moveInDate', formData.moveInDate);
    formBody.append('budget', formData.budget);
    formBody.append('bedrooms', formData.bedrooms);
    formBody.append('pets', formData.pets);
    formBody.append('employmentStatus', formData.employmentStatus);
    formBody.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d672mbkfomltbg1qamsg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          moveInDate: '',
          budget: '',
          bedrooms: '',
          pets: '',
          employmentStatus: '',
          message: ''
        });
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'What credit score do I need to rent?',
      answer: 'Most landlords in Miami require a minimum credit score of 620-650. However, we work with properties that have flexible requirements. If your credit is below average, we can help you find options that accept additional deposits or co-signers.'
    },
    {
      question: 'Are pets allowed in rental properties?',
      answer: 'Pet policies vary by property. Many rentals in Miami accept pets with a pet deposit (typically $200-$500) and monthly pet rent ($25-$50). We always verify pet policies upfront and can filter properties based on your pet needs.'
    },
    {
      question: 'What documents do I need for the application?',
      answer: "Typically you will need: valid ID (passport or driver's license), proof of income (pay stubs or bank statements), employment verification letter, previous landlord references, and completed rental application. International applicants may need additional documentation."
    },
    {
      question: 'How much should I budget for move-in costs?',
      answer: "Plan for first month's rent, security deposit (usually equal to one month's rent), and application fees ($50-$100 per adult). Some properties may require last month's rent upfront. Total move-in costs typically range from 2-3 months' rent."
    },
    {
      question: 'How long does the application process take?',
      answer: 'Once you submit a complete application, approval typically takes 24-72 hours. We expedite the process by pre-screening your documents and coordinating directly with property managers to ensure fast turnaround.'
    },
    {
      question: 'Do you charge any fees for your services?',
      answer: 'Our rental search service is completely free for tenants. We are compensated by property owners and management companies. There are no hidden fees—what you see is what you pay.'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-['Quicksand']">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] sm:min-h-[70vh] md:min-h-[80vh] w-full overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <img
            ref={parallaxRef}
            src="https://public.readdy.ai/ai/img_res/e32a3a96a1995bf87a9cb02950bb4dbb.jpg"
            alt="Miami Rental Properties"
            className="w-full h-full object-cover object-center will-change-transform"
            style={{ transition: 'transform 0.1s linear' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-black/80 via-black/60 to-black/50 sm:from-black/70 sm:via-black/50 sm:to-black/40"></div>
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20 pt-20 sm:pt-24 md:pt-32 pb-8 sm:pb-0">
          <div className="max-w-[700px]">
            <div
              className={`inline-block px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 rounded-full bg-[#B4E4E0]/20 backdrop-blur-sm mb-4 sm:mb-6 md:mb-8 border border-[#B4E4E0]/30 transition-all duration-700 ease-out ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <span className="text-white text-[11px] sm:text-[12px] md:text-[13px] font-medium tracking-wide">Available Rentals</span>
            </div>

            <h1
              className={`font-['Oxygen'] font-bold text-[28px] sm:text-[36px] md:text-[52px] lg:text-[60px] leading-[1.15] sm:leading-[1.1] text-white mb-4 sm:mb-5 md:mb-6 tracking-tight transition-all duration-700 ease-out delay-150 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              Miami Rental Properties | Available Apartments & Homes
            </h1>

            <p
              className={`text-white/90 text-[15px] sm:text-[18px] md:text-[20px] lg:text-[22px] leading-[1.5] sm:leading-[1.6] mb-6 sm:mb-8 md:mb-10 max-w-[600px] transition-all duration-700 ease-out delay-300 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              Pre-screened properties, professional service, bilingual support
            </p>

            <a
              href="#apply"
              className={`inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-7 md:px-8 py-3.5 sm:py-4 bg-[#A7D0CC] rounded-xl sm:rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 cursor-pointer shadow-lg hover:shadow-[0_4px_12px_rgba(167,208,204,0.3)] min-h-[52px] sm:min-h-[48px] whitespace-nowrap active:scale-[0.98] ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionProperty: 'opacity, transform, background-color, box-shadow', transitionDuration: '700ms, 700ms, 300ms, 300ms', transitionDelay: '450ms, 450ms, 0ms, 0ms', transitionTimingFunction: 'ease-out' }}
            >
              <span className="text-black text-[15px] sm:text-[16px] font-bold font-['Oxygen']">Start Your Search</span>
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 md:py-28 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20">
          <div className="text-center mb-10 sm:mb-14 md:mb-20">
            <h2 className="font-['Oxygen'] font-bold text-[28px] sm:text-[36px] md:text-[48px] lg:text-[56px] text-black mb-3 sm:mb-4 md:mb-6">
              How It Works
            </h2>
            <p className="text-[#666666] text-[15px] sm:text-[17px] md:text-[20px] max-w-[500px] mx-auto">
              Three simple steps to find your perfect rental in Miami
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
            {[
              {
                number: '01',
                icon: 'ri-search-line',
                title: 'Browse Listings',
                description: 'Explore our curated selection of MLS-verified rental properties across Miami. Filter by location, price, and amenities.'
              },
              {
                number: '02',
                icon: 'ri-file-list-3-line',
                title: 'Quick Pre-Qualification',
                description: 'Fill out our simple pre-qualification form. We review your information and match you with suitable properties.'
              },
              {
                number: '03',
                icon: 'ri-calendar-check-line',
                title: 'Schedule Showing',
                description: 'Book private viewings at your convenience. Our bilingual agents guide you through every step until move-in.'
              }
            ].map((step, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-6 sm:p-8 md:p-10 border-2 border-[#E8E2DB] hover:border-[#A7D0CC] transition-all duration-300">
                <div className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8 font-['Oxygen'] font-bold text-[80px] sm:text-[100px] md:text-[120px] leading-none text-[#A7D0CC]/30 pointer-events-none select-none">
                  {step.number}
                </div>
                <div className="relative z-10">
                  <div className="w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 rounded-full bg-[#A7D0CC] flex items-center justify-center mb-5 sm:mb-6 md:mb-8">
                    <i className={`${step.icon} text-[24px] sm:text-[28px] md:text-[32px] text-black`}></i>
                  </div>
                  <h3 className="font-['Oxygen'] font-bold text-[20px] sm:text-[24px] md:text-[28px] text-black mb-2 sm:mb-3 md:mb-4">
                    {step.title}
                  </h3>
                  <p className="text-[#666666] text-[14px] sm:text-[15px] md:text-[16px] leading-[1.6] sm:leading-[1.7]">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Rent with MCG */}
      <section className="bg-[#E8E2DB] py-14 sm:py-16 md:py-24 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#B4E4E0]/40 mb-3 sm:mb-4 md:mb-6">
                <span className="text-black text-[11px] sm:text-[12px] md:text-[14px] uppercase tracking-[2px] font-medium">Why Choose Us</span>
              </div>
              <h2 className="font-['Oxygen'] font-bold text-[26px] sm:text-[32px] md:text-[44px] lg:text-[52px] leading-[1.15] text-black mb-4 sm:mb-6 md:mb-8">
                Why Rent with<br />Miami Comfort Group
              </h2>
              <p className="text-[#333333] text-[14px] sm:text-[16px] md:text-[18px] leading-[1.6] sm:leading-[1.7] mb-6 sm:mb-8 md:mb-10">
                We make finding your next home simple, transparent, and stress-free. Our team is dedicated to matching you with the perfect property.
              </p>

              <div className="space-y-4 sm:space-y-5 md:space-y-6">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Licensed Real Estate Agent',
                    description: 'Work with a licensed professional who knows the Miami market inside and out'
                  },
                  {
                    icon: 'ri-verified-badge-line',
                    title: 'MLS Verified Properties',
                    description: 'All listings are verified through the Multiple Listing Service for accuracy'
                  },
                  {
                    icon: 'ri-money-dollar-circle-line',
                    title: 'No Hidden Fees',
                    description: 'Our service is free for tenants—no surprise charges or commissions'
                  },
                  {
                    icon: 'ri-translate-2',
                    title: 'Bilingual Support',
                    description: 'Full service in English and Spanish for seamless communication'
                  },
                  {
                    icon: 'ri-timer-flash-line',
                    title: 'Fast Response Time',
                    description: 'Quick replies and efficient scheduling to help you move faster'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4 group">
                    <div className="w-10 sm:w-11 md:w-12 h-10 sm:h-11 md:h-12 flex-shrink-0 rounded-xl bg-white flex items-center justify-center group-hover:bg-[#B4E4E0] transition-colors">
                      <i className={`${benefit.icon} text-[20px] sm:text-[22px] md:text-[24px] text-black`}></i>
                    </div>
                    <div>
                      <h3 className="font-['Oxygen'] font-bold text-[15px] sm:text-[16px] md:text-[18px] text-black mb-0.5 sm:mb-1">
                        {benefit.title}
                      </h3>
                      <p className="text-[#666666] text-[13px] sm:text-[14px] md:text-[15px] leading-[1.5] sm:leading-[1.6]">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20Miami%20apartment%20interior%20living%20room%20with%20large%20windows%20showing%20city%20view%2C%20contemporary%20furniture%20in%20neutral%20tones%2C%20bright%20natural%20lighting%2C%20minimalist%20luxury%20design%20with%20tropical%20plants%2C%20real%20estate%20photography%20style&width=800&height=900&seq=rentals-why-001&orientation=portrait"
                alt="Miami Apartment Interior"
                className="w-full h-[350px] sm:h-[450px] md:h-[600px] lg:h-[700px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Qualification Form */}
      <section id="apply" className="bg-black py-14 sm:py-16 md:py-24 lg:py-28">
        <div className="max-w-[800px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#B4E4E0]/20 mb-3 sm:mb-4 md:mb-6">
              <span className="text-white text-[11px] sm:text-[12px] md:text-[14px] uppercase tracking-[2px] font-medium">Get Started</span>
            </div>
            <h2 className="font-['Oxygen'] font-bold text-[26px] sm:text-[32px] md:text-[44px] lg:text-[52px] leading-[1.2] text-white mb-3 sm:mb-4">
              Quick Pre-Qualification
            </h2>
            <p className="text-white/80 text-[14px] sm:text-[16px] md:text-[18px] max-w-[500px] mx-auto">
              Fill out this form and we'll match you with available properties
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-[#B4E4E0]/20 border border-[#B4E4E0]/40 rounded-2xl p-6 sm:p-8 md:p-12 text-center">
              <div className="w-14 sm:w-16 h-14 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-full bg-[#B4E4E0] flex items-center justify-center">
                <i className="ri-check-line text-[28px] sm:text-[32px] text-black"></i>
              </div>
              <h3 className="font-['Oxygen'] font-bold text-[22px] sm:text-[24px] md:text-[28px] text-white mb-3 sm:mb-4">
                Application Received!
              </h3>
              <p className="text-white/80 text-[14px] sm:text-[16px] md:text-[18px] mb-5 sm:mb-6">
                Thank you for your interest. Our team will review your information and contact you within 24 hours.
              </p>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="text-[#B4E4E0] text-[14px] sm:text-[15px] font-medium hover:underline cursor-pointer"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <form
              id="rental-prequalification-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4 sm:space-y-5 md:space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px]"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px]"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px]"
                  required
                />
                <input
                  type="date"
                  name="moveInDate"
                  placeholder="Desired Move-in Date"
                  value={formData.moveInDate}
                  onChange={(e) => setFormData({ ...formData, moveInDate: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px] cursor-pointer appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  required
                >
                  <option value="" className="bg-black">Monthly Budget *</option>
                  <option value="under-1500" className="bg-black">Under $1,500</option>
                  <option value="1500-2000" className="bg-black">$1,500 - $2,000</option>
                  <option value="2000-2500" className="bg-black">$2,000 - $2,500</option>
                  <option value="2500-3000" className="bg-black">$2,500 - $3,000</option>
                  <option value="3000-4000" className="bg-black">$3,000 - $4,000</option>
                  <option value="over-4000" className="bg-black">Over $4,000</option>
                </select>
                <select
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  className="w-full h-12 sm:h-14 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 text-white focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px] cursor-pointer appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  required
                >
                  <option value="" className="bg-black">Bedrooms Needed *</option>
                  <option value="studio" className="bg-black">Studio</option>
                  <option value="1" className="bg-black">1 Bedroom</option>
                  <option value="2" className="bg-black">2 Bedrooms</option>
                  <option value="3" className="bg-black">3 Bedrooms</option>
                  <option value="4+" className="bg-black">4+ Bedrooms</option>
                </select>
              </div>

              <textarea
                name="message"
                placeholder="Additional information (preferred areas, special requirements, etc.)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                className="w-full h-24 sm:h-28 bg-transparent border border-white/30 rounded-xl px-4 sm:px-5 py-3 sm:py-4 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[14px] sm:text-[15px] resize-none"
                maxLength={500}
              ></textarea>

              {submitError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 sm:p-4 text-red-300 text-[13px] sm:text-[14px]">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-[52px] sm:h-[56px] bg-[#A7D0CC] rounded-xl sm:rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 flex items-center justify-center cursor-pointer shadow-lg hover:shadow-[0_4px_12px_rgba(167,208,204,0.3)] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              >
                <span className="font-['Oxygen'] font-bold text-[15px] sm:text-[16px] text-black">
                  {isSubmitting ? 'Submitting...' : 'Submit Pre-Qualification'}
                </span>
              </button>

              <p className="text-white/50 text-[12px] sm:text-[13px] text-center">
                ¿Prefieres español? <Link to="/alquileres" className="text-[#B4E4E0] hover:underline cursor-pointer">Completa el formulario en español</Link>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-14 sm:py-16 md:py-24 lg:py-28">
        <div className="max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#E8E2DB] mb-3 sm:mb-4 md:mb-6">
              <span className="text-black text-[11px] sm:text-[12px] md:text-[14px] uppercase tracking-[2px] font-medium">Common Questions</span>
            </div>
            <h2 className="font-['Oxygen'] font-bold text-[26px] sm:text-[32px] md:text-[44px] lg:text-[52px] leading-[1.2] text-black mb-3 sm:mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-[#666666] text-[14px] sm:text-[16px] md:text-[18px]">
              Everything you need to know about renting in Miami
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#E8E2DB] rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 sm:p-5 md:p-6 text-left hover:bg-[#E8E2DB]/30 transition-colors cursor-pointer active:bg-[#E8E2DB]/50"
                >
                  <h4 className="font-['Oxygen'] font-bold text-[14px] sm:text-[16px] md:text-[18px] text-black pr-3 sm:pr-4 leading-[1.4]">
                    {faq.question}
                  </h4>
                  <div className="w-7 sm:w-8 h-7 sm:h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E8E2DB]/50">
                    <i className={`ri-arrow-down-s-line text-[20px] sm:text-[24px] text-black transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6">
                    <p className="text-[#666666] text-[13px] sm:text-[15px] md:text-[16px] leading-[1.6] sm:leading-[1.7]">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-[#E8E2DB] py-10 sm:py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5 sm:gap-6 md:gap-10 text-center md:text-left">
            <div>
              <h3 className="font-['Oxygen'] font-bold text-[22px] sm:text-[24px] md:text-[32px] text-black mb-1 sm:mb-2">
                Questions? Let's Talk
              </h3>
              <p className="text-[#666666] text-[14px] sm:text-[15px] md:text-[16px]">
                Contact Elisabet directly for personalized assistance
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 md:gap-6 w-full sm:w-auto">
              <a
                href="tel:+17868568145"
                className="flex items-center justify-center gap-3 px-5 sm:px-6 py-3 sm:py-3 bg-white rounded-xl hover:shadow-md transition-all cursor-pointer w-full sm:w-auto active:scale-[0.98]"
              >
                <i className="ri-phone-line text-[18px] sm:text-[20px] text-[#B4E4E0]"></i>
                <span className="font-['Oxygen'] font-bold text-[14px] sm:text-[15px] text-black">+1 786 856 8145</span>
              </a>
              <a
                href="https://wa.me/17868568145"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 px-5 sm:px-6 py-3 sm:py-3 bg-[#25D366] rounded-xl hover:bg-[#20BD5A] transition-all cursor-pointer w-full sm:w-auto active:scale-[0.98]"
              >
                <i className="ri-whatsapp-line text-[18px] sm:text-[20px] text-white"></i>
                <span className="font-['Oxygen'] font-bold text-[14px] sm:text-[15px] text-white whitespace-nowrap">WhatsApp Elisabet</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 font-['Oxygen'] font-bold text-[80px] sm:text-[100px] md:text-[140px] lg:text-[180px] text-white/[0.03] text-center leading-none pointer-events-none select-none" style={{ transform: 'translateY(40px)' }}>
          MCG
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-20 py-10 sm:py-12 md:py-16 lg:py-20">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12 lg:gap-16">
            <div className="col-span-2 md:col-span-2 lg:col-span-1">
              <img
                src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png"
                alt="Miami Comfort Group"
                className="h-8 sm:h-10 md:h-12 w-auto object-contain mb-6 sm:mb-8 md:mb-12 brightness-0 invert"
              />
              <h3 className="font-['Oxygen'] font-bold text-[20px] sm:text-[24px] md:text-[32px] text-white leading-[1.2] mb-4 sm:mb-6 md:mb-8">
                Strategic Real Estate<br />Consulting
              </h3>
              <p className="text-white/70 text-[13px] sm:text-[14px] md:text-[15px] leading-[1.6]">
                Investing well starts with deciding well
              </p>
            </div>

            <div>
              <h4 className="font-['Oxygen'] font-bold text-[12px] sm:text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-3 sm:mb-4 md:mb-6">Services</h4>
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                {[
                  'Strategic Consulting',
                  'Company & Legal Structure',
                  'Financing & Loans',
                  'Market Studies',
                  'Property Search',
                  'Property Management'
                ].map((link, index) => (
                  <div key={index}>
                    <Link to="/services" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                      {link}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-['Oxygen'] font-bold text-[12px] sm:text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-3 sm:mb-4 md:mb-6">Company</h4>
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <Link to="/about" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  About Us
                </Link>
                <Link to="/services" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Services
                </Link>
                <Link to="/relocation" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Relocation
                </Link>
                <Link to="/rentals" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Rentals
                </Link>
                <Link to="/#contact" className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Contact
                </Link>
              </div>
            </div>

            <div>
              <h4 className="font-['Oxygen'] font-bold text-[12px] sm:text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-3 sm:mb-4 md:mb-6">Contact</h4>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-user-line text-[#B4E4E0] text-[14px] sm:text-[16px]"></i>
                  <span className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px]">Elisabet Sierra Bermejo</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-[#B4E4E0] text-[14px] sm:text-[16px]"></i>
                  <span className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px]">+1 786 856 8145</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-map-pin-line text-[#B4E4E0] text-[14px] sm:text-[16px]"></i>
                  <span className="text-white/80 text-[13px] sm:text-[14px] md:text-[15px]">Miami, FL, USA</span>
                </div>
              </div>

              <div className="flex items-center gap-3 sm:gap-4 md:gap-5 mt-6 sm:mt-8 md:mt-10">
                <a href="https://www.instagram.com/elisabet_comfyhouses/" target="_blank" rel="noopener noreferrer" className="w-8 sm:w-6 h-8 sm:h-6 flex items-center justify-center cursor-pointer">
                  <i className="ri-instagram-line text-[20px] sm:text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                </a>
                <a href="https://www.linkedin.com/in/elisabet-sierra/" target="_blank" rel="noopener noreferrer" className="w-8 sm:w-6 h-8 sm:h-6 flex items-center justify-center cursor-pointer">
                  <i className="ri-linkedin-line text-[20px] sm:text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-8 sm:w-6 h-8 sm:h-6 flex items-center justify-center cursor-pointer">
                  <i className="ri-youtube-line text-[20px] sm:text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 sm:mt-12 md:mt-16 lg:mt-20 pt-5 sm:pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-white/50 text-[12px] sm:text-[13px] md:text-[14px] text-center md:text-left">
              © 2026 Miami Comfort Group. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 text-[12px] sm:text-[13px] md:text-[14px] hover:text-[#B4E4E0] transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
          
          {/* Legal Text Block */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center space-y-3">
            <p className="text-[#cccccc] font-bold text-[11px] leading-[1.6]">
              Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
              Avanti Way Realty, LLC · License #SL3535387
            </p>
            <p className="text-[#888888] text-[10px] leading-[1.5] max-w-[800px] mx-auto">
              Miami Comfort Group LLC is a brand name used for marketing purposes only. Real estate services are provided by Elisabet Sierra Bermejo, licensed with Avanti Way Realty, LLC.
            </p>
            <p className="text-[#888888] text-[10px] leading-[1.5]">
              © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
            </p>
            <p className="text-[#888888] text-[9px] leading-[1.5] max-w-[700px] mx-auto">
              Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
